import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Star } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { CommunicationsPageShell } from "../shared/CommunicationsPageShell";
import { useCommunicationLocales } from "./use-communication-locales";
import { CreateCommunicationLocaleDialog } from "./CreateCommunicationLocaleDialog";

export function CommunicationLocalesPage() {
  const { t } = useTranslation();
  const { data: items, isLoading, isError } = useCommunicationLocales();
  const [createOpen, setCreateOpen] = useState(false);

  const headerActions = (
    <Button data-testid="communication-new-locale-button" onClick={() => setCreateOpen(true)}>
      <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
      {t("communications.locales.create.button")}
    </Button>
  );

  return (
    <CommunicationsPageShell
      screenTestId="communication-locales-screen"
      titleKey="communications.locales.title"
      subtitleKey="communications.locales.subtitle"
      headerActions={headerActions}
      showTabs
    >
      {isLoading && (
        <p data-testid="communication-locales-loading" className="text-sm text-muted-foreground">
          {t("communications.locales.loading")}
        </p>
      )}

      {isError && (
        <p data-testid="communication-locales-error" className="text-sm text-destructive">
          {t("communications.locales.loadError")}
        </p>
      )}

      {items && items.length === 0 && (
        <p data-testid="communication-locales-empty" className="text-sm text-muted-foreground">
          {t("communications.locales.empty")}
        </p>
      )}

      {items && items.length > 0 && (
        <ul className="space-y-2">
          {items.map((locale) => (
            <li
              key={locale.code}
              data-testid={`communication-locale-row-${locale.code}`}
              className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-card p-4"
            >
              <div className="flex items-center gap-2">
                <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
                  {locale.code}
                </code>
                <span className="text-sm text-muted-foreground">{locale.language}</span>
                {locale.isDefault && (
                  <span
                    data-testid={`communication-locale-default-${locale.code}`}
                    className="inline-flex items-center gap-1 rounded-full bg-wander-orange/15 px-2 py-0.5 text-xs text-wander-orange ring-1 ring-inset ring-wander-orange/40"
                  >
                    <Star className="h-3 w-3" aria-hidden="true" />
                    {t("communications.locales.defaultBadge")}
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <CreateCommunicationLocaleDialog open={createOpen} onOpenChange={setCreateOpen} />
    </CommunicationsPageShell>
  );
}
